import { storage, db } from '../firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  appendChunk,
  markChunkUploaded,
  bumpChunkFailure,
  updateUpload,
  deleteUpload,
  getUpload
} from './uploadPersistence';
import { mediaRecordingDocPath, MAX_VIDEO_DURATION_MS, MAX_AUDIO_DURATION_MS } from './recordingService';

// Global concurrency cap: no more than N chunk PUTs in-flight across all
// StreamingUploader instances. Keeps the network and memory pressure
// predictable when several recordings race to upload.
const MAX_PARALLEL_CHUNKS = 3;
let activeUploads = 0;
const waitQueue = [];
async function acquireSlot() {
  if (activeUploads < MAX_PARALLEL_CHUNKS) {
    activeUploads += 1;
    return;
  }
  await new Promise((resolve) => waitQueue.push(resolve));
  activeUploads += 1;
}
function releaseSlot() {
  activeUploads = Math.max(0, activeUploads - 1);
  const next = waitQueue.shift();
  if (next) next();
}

const CHUNK_PATH = (userId, recordingId, index) =>
  `recordings/${userId}/streaming/${recordingId}/chunk-${String(index).padStart(4, '0')}.webm`;
const MANIFEST_PATH = (userId, recordingId) =>
  `recordings/${userId}/streaming/${recordingId}/_manifest.json`;

const MAX_CHUNK_RETRIES = 3;
const BASE_BACKOFF_MS = 800;
const DEFAULT_TIMESLICE_MS = 3000;

/**
 * StreamingUploader — owns one recording from getUserMedia(...) through
 * manifest finalization.
 *
 * Contract:
 *   const up = new StreamingUploader({ userId, recordingId, mediaType,
 *     itemId, sessionId, stream, mimeType, ownerType, onStatus });
 *   await up.start();
 *   ...
 *   const meta = await up.stop();   // resolves with the recording metadata
 *                                   // once the manifest is persisted
 *
 * Status callbacks: 'uploading' -> 'finalizing' -> 'processing' (handed off
 * to the assembly + transcode CFs). 'failed' on hard errors. The in-memory
 * progress counter is also pushed via onStatus so the global pill can render
 * a percent.
 */
export class StreamingUploader {
  constructor({
    userId,
    recordingId,
    mediaType,
    itemId,
    sessionId,
    stream,
    mimeType,
    ownerType = 'session',
    videoBitsPerSecond = 2_500_000,
    audioBitsPerSecond = 128_000,
    timesliceMs = DEFAULT_TIMESLICE_MS,
    onStatus = () => {},
    onMaxDurationReached = null
  }) {
    this.userId = userId;
    this.recordingId = recordingId || `recording_${Date.now()}`;
    this.mediaType = mediaType === 'video' ? 'video' : 'audio';
    this.itemId = itemId;
    this.sessionId = sessionId;
    this.ownerType = ownerType;
    this.stream = stream;
    this.mimeType = mimeType;
    this.videoBitsPerSecond = videoBitsPerSecond;
    this.audioBitsPerSecond = audioBitsPerSecond;
    this.timesliceMs = timesliceMs;
    this.onStatus = onStatus;
    this.onMaxDurationReached = onMaxDurationReached;

    this.mediaRecorder = null;
    this.chunks = []; // local copy of indexes seen so far
    this.nextChunkIndex = 0;
    this.totalBytes = 0;
    this.uploadedBytes = 0;
    this.startedAt = null;
    this.endedAt = null;
    this.status = 'idle';
    this.maxDurationTimer = null;
    // Promise that resolves once the MediaRecorder's final ondataavailable
    // has fired. stop() uses this to know when it's safe to upload the
    // manifest.
    this._recorderFinalized = null;
    this._resolveRecorderFinalized = null;

    // Tracks per-chunk upload promises so stop() can wait on them.
    this._chunkPromises = new Map();
  }

  async start() {
    if (!this.stream) throw new Error('StreamingUploader.start requires a MediaStream');
    this._setStatus('uploading');

    // Seed IDB + Firestore up front so a crash mid-recording still leaves
    // enough breadcrumbs to resume.
    await updateUpload(this.recordingId, (rec) => ({
      ...rec,
      recordingId: this.recordingId,
      userId: this.userId,
      mediaType: this.mediaType,
      itemId: this.itemId,
      sessionId: this.sessionId,
      ownerType: this.ownerType,
      mimeType: this.mimeType,
      createdAt: rec.createdAt || Date.now(),
      chunks: rec.chunks || [],
      manifestUploaded: rec.manifestUploaded || false,
      assembled: false
    }));
    await this._writeMediaDoc({ status: 'uploading', progress: 0 });

    const recorderOptions =
      this.mediaType === 'video'
        ? { mimeType: this.mimeType, videoBitsPerSecond: this.videoBitsPerSecond, audioBitsPerSecond: this.audioBitsPerSecond }
        : { mimeType: this.mimeType, audioBitsPerSecond: this.audioBitsPerSecond };

    this.mediaRecorder = new MediaRecorder(this.stream, recorderOptions);
    this._recorderFinalized = new Promise((resolve) => {
      this._resolveRecorderFinalized = resolve;
    });

    this.mediaRecorder.ondataavailable = async (event) => {
      if (!event.data || event.data.size === 0) return;
      const index = this.nextChunkIndex++;
      const blob = event.data;
      this.totalBytes += blob.size;
      this.chunks.push({ index, size: blob.size, uploaded: false });
      try {
        await appendChunk(this.recordingId, index, blob);
      } catch (e) {
        // IDB write failure shouldn't block the upload — we still try.
        console.warn('[streaming] appendChunk to IDB failed:', e);
      }
      this._emitProgress();
      this._scheduleChunkUpload(index, blob);
    };

    this.mediaRecorder.onstop = () => {
      if (this._resolveRecorderFinalized) this._resolveRecorderFinalized();
    };

    this.mediaRecorder.start(this.timesliceMs);
    this.startedAt = Date.now();

    const cap = this.mediaType === 'video' ? MAX_VIDEO_DURATION_MS : MAX_AUDIO_DURATION_MS;
    this.maxDurationTimer = setTimeout(() => {
      if (this.status === 'uploading') {
        console.warn('[streaming] max duration reached, auto-stopping');
        if (this.onMaxDurationReached) {
          try { this.onMaxDurationReached(); } catch (e) { /* noop */ }
        }
      }
    }, cap);
  }

  _setStatus(next, extra = {}) {
    this.status = next;
    try {
      this.onStatus({
        recordingId: this.recordingId,
        status: next,
        mediaType: this.mediaType,
        itemId: this.itemId,
        sessionId: this.sessionId,
        bytesUploaded: this.uploadedBytes,
        totalBytes: this.totalBytes,
        progress: this._percent(),
        ...extra
      });
    } catch (e) {
      // Caller errors shouldn't take down the uploader.
    }
  }

  _percent() {
    if (!this.totalBytes) return 0;
    return Math.min(100, Math.round((this.uploadedBytes / this.totalBytes) * 100));
  }

  _emitProgress() {
    this._setStatus(this.status);
  }

  _scheduleChunkUpload(index, blob) {
    const task = (async () => {
      let attempt = 0;
      while (attempt < MAX_CHUNK_RETRIES) {
        attempt += 1;
        try {
          await acquireSlot();
          try {
            await this._uploadSingleChunk(index, blob);
            this.uploadedBytes += blob.size;
            const entry = this.chunks.find((c) => c.index === index);
            if (entry) entry.uploaded = true;
            await markChunkUploaded(this.recordingId, index);
            await this._writeMediaDoc({ progress: this._percent() });
            this._emitProgress();
            return true;
          } finally {
            releaseSlot();
          }
        } catch (err) {
          console.warn(`[streaming] chunk ${index} upload failed attempt ${attempt}:`, err?.message || err);
          await bumpChunkFailure(this.recordingId, index, err?.message || String(err));
          if (attempt >= MAX_CHUNK_RETRIES) {
            this._setStatus('failed', { errorMessage: `Chunk ${index}: ${err?.message || err}` });
            return false;
          }
          const delay = BASE_BACKOFF_MS * Math.pow(2, attempt - 1);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      return false;
    })();
    this._chunkPromises.set(index, task);
  }

  async _uploadSingleChunk(index, blob) {
    const path = CHUNK_PATH(this.userId, this.recordingId, index);
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob, {
      contentType: blob.type || (this.mediaType === 'video' ? 'video/webm' : 'audio/webm'),
      customMetadata: {
        mediaType: this.mediaType,
        recordingId: this.recordingId,
        userId: this.userId,
        chunkIndex: String(index)
      }
    });
  }

  async _writeMediaDoc(patch) {
    try {
      const docRef = doc(db, mediaRecordingDocPath(this.userId, this.recordingId));
      await setDoc(
        docRef,
        {
          id: this.recordingId,
          userId: this.userId,
          mediaType: this.mediaType,
          ownerType: this.ownerType,
          ownerId: this.sessionId || null,
          practiceItemId: this.itemId || null,
          sessionId: this.sessionId || null,
          viewerUids: [],
          updatedAt: serverTimestamp(),
          ...patch
        },
        { merge: true }
      );
    } catch (e) {
      // The client doc is a nice-to-have; the assembly CF will backfill
      // status on its side if this write fails.
      console.warn('[streaming] writeMediaDoc failed:', e?.message);
    }
  }

  // stop() — finalize the recording. Resolves with the recording metadata
  // object that practice/lesson code can stash on the session/lesson doc.
  async stop() {
    if (this.status !== 'uploading') {
      // Already stopped / failed — just return a best-effort metadata.
      return this._buildMetadata();
    }
    this._setStatus('finalizing');

    if (this.maxDurationTimer) { clearTimeout(this.maxDurationTimer); this.maxDurationTimer = null; }

    try {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      await this._recorderFinalized; // waits for ondataavailable of the tail chunk
    } catch (e) {
      console.warn('[streaming] mediaRecorder.stop threw:', e?.message);
    }

    // Release the capture tracks so the camera/mic indicator turns off
    // immediately — the user has already clicked Stop, no reason to hold
    // them while chunks drain.
    try {
      this.stream.getTracks().forEach((t) => {
        try { t.stop(); } catch (e) { /* noop */ }
      });
    } catch (e) { /* noop */ }

    this.endedAt = Date.now();

    // Wait for every chunk upload to settle.
    const chunkTasks = Array.from(this._chunkPromises.values());
    const results = await Promise.all(chunkTasks.map((p) => p.catch(() => false)));
    const anyChunkFailed = results.some((r) => r === false);

    if (anyChunkFailed) {
      this._setStatus('failed', { errorMessage: 'One or more chunks failed to upload' });
      return this._buildMetadata();
    }

    // Write manifest — this is the trigger the assembly CF listens for.
    const manifest = {
      recordingId: this.recordingId,
      userId: this.userId,
      mediaType: this.mediaType,
      itemId: this.itemId,
      sessionId: this.sessionId,
      ownerType: this.ownerType,
      mimeType: this.mimeType,
      totalChunks: this.nextChunkIndex,
      totalBytes: this.totalBytes,
      startedAt: this.startedAt,
      endedAt: this.endedAt
    };

    try {
      const manifestRef = ref(storage, MANIFEST_PATH(this.userId, this.recordingId));
      await uploadBytes(manifestRef, new Blob([JSON.stringify(manifest)], { type: 'application/json' }), {
        contentType: 'application/json',
        customMetadata: {
          mediaType: this.mediaType,
          recordingId: this.recordingId,
          userId: this.userId,
          ownerType: this.ownerType,
          ownerId: String(this.sessionId || '')
        }
      });
    } catch (e) {
      this._setStatus('failed', { errorMessage: `Manifest upload failed: ${e?.message || e}` });
      return this._buildMetadata();
    }

    await updateUpload(this.recordingId, (rec) => ({ ...rec, manifestUploaded: true }));
    await this._writeMediaDoc({ status: 'processing', progress: 100, totalChunks: this.nextChunkIndex });

    this._setStatus('processing');
    return this._buildMetadata();
  }

  // cancel() — user discards the recording (or an unrecoverable error).
  async cancel(reason) {
    try {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    } catch (e) { /* noop */ }
    try {
      this.stream?.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
    } catch (e) { /* noop */ }
    if (this.maxDurationTimer) { clearTimeout(this.maxDurationTimer); this.maxDurationTimer = null; }
    this._setStatus('failed', { errorMessage: reason || 'Cancelled' });
    try { await this._writeMediaDoc({ status: 'failed', error: reason || 'Cancelled' }); } catch (e) { /* noop */ }
  }

  _buildMetadata() {
    const now = Date.now();
    const duration = this.startedAt ? Math.max(0, Math.round(((this.endedAt || now) - this.startedAt) / 1000)) : 0;
    return {
      id: this.recordingId,
      practiceItemId: this.itemId,
      sessionId: this.sessionId,
      mediaType: this.mediaType,
      status: this.status === 'processing' ? 'processing' : this.status,
      recordingDocPath: mediaRecordingDocPath(this.userId, this.recordingId),
      // audioUrl/hlsPath/posterPath get filled in later by the transcode CF.
      audioUrl: null,
      previewUrl: null,
      hlsPath: null,
      posterPath: null,
      storagePath: `recordings/${this.userId}/raw/${this.mediaType}/${this.recordingId}.webm`,
      timestamp: this.startedAt || now,
      size: this.totalBytes,
      duration,
      comments: [],
      regions: [],
      markers: [],
      createdAt: new Date(this.startedAt || now),
      isLocal: false,
      // Used by UI badges / retry buttons.
      isStreaming: true
    };
  }
}

/**
 * Resume a previously-started streaming upload from IDB.
 *
 * - If chunks are still in IDB unuploaded, re-schedule them.
 * - If chunks are all uploaded but manifest wasn't, re-upload the manifest.
 * - If everything succeeded and we just missed the assembly, call the
 *   assembly-not-yet-triggered branch (simply re-writing the manifest causes
 *   the CF to re-fire since it's idempotent).
 *
 * Returns the recording metadata (same shape as StreamingUploader.stop())
 * or null if the record is unrecoverable.
 */
export async function resumeStreamingUpload(recordingId) {
  const record = await getUpload(recordingId);
  if (!record) return null;

  const { userId, mediaType, itemId, sessionId, ownerType, mimeType } = record;
  const chunks = record.chunks || [];
  const unsentWithBlob = chunks.filter((c) => !c.uploaded && c.blob);
  const unsentWithoutBlob = chunks.filter((c) => !c.uploaded && !c.blob);

  // No blobs to resend for missing chunks — recording is unrecoverable.
  if (unsentWithoutBlob.length > 0) {
    await updateUpload(recordingId, (rec) => ({ ...rec, assembled: true, status: 'failed' }));
    return null;
  }

  // Re-upload any pending chunks serially (simpler for resume).
  for (const chunk of unsentWithBlob) {
    const path = CHUNK_PATH(userId, recordingId, chunk.index);
    try {
      await uploadBytes(ref(storage, path), chunk.blob, {
        contentType: chunk.blob.type || 'application/octet-stream',
        customMetadata: { mediaType, recordingId, userId, chunkIndex: String(chunk.index) }
      });
      await markChunkUploaded(recordingId, chunk.index);
    } catch (e) {
      console.warn('[streaming] resume chunk upload failed', chunk.index, e?.message);
      return null;
    }
  }

  // (Re-)write the manifest so the assembly CF fires.
  const totalChunks = chunks.length;
  const totalBytes = chunks.reduce((acc, c) => acc + (c.size || 0), 0);
  const manifest = {
    recordingId,
    userId,
    mediaType,
    itemId,
    sessionId,
    ownerType: ownerType || 'session',
    mimeType: mimeType || 'video/webm',
    totalChunks,
    totalBytes,
    startedAt: record.createdAt || Date.now(),
    endedAt: Date.now(),
    resumed: true
  };
  try {
    const manifestRef = ref(storage, MANIFEST_PATH(userId, recordingId));
    await uploadBytes(manifestRef, new Blob([JSON.stringify(manifest)], { type: 'application/json' }), {
      contentType: 'application/json',
      customMetadata: {
        mediaType,
        recordingId,
        userId,
        ownerType: ownerType || 'session',
        ownerId: String(sessionId || '')
      }
    });
  } catch (e) {
    console.warn('[streaming] resume manifest upload failed:', e?.message);
    return null;
  }

  // Mark as assembled from the client's point of view — we've pushed everything
  // we can. The assembly/transcode CFs take over from here. If they haven't run
  // yet, the server-side state check in the resume reconciler will catch it.
  await updateUpload(recordingId, (rec) => ({ ...rec, manifestUploaded: true, assembled: true }));
  try {
    await setDoc(
      doc(db, mediaRecordingDocPath(userId, recordingId)),
      {
        id: recordingId,
        userId,
        mediaType,
        status: 'processing',
        progress: 100,
        totalChunks,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (e) { /* noop */ }

  return {
    id: recordingId,
    practiceItemId: itemId,
    sessionId,
    mediaType,
    status: 'processing',
    recordingDocPath: mediaRecordingDocPath(userId, recordingId),
    audioUrl: null,
    previewUrl: null,
    hlsPath: null,
    posterPath: null,
    storagePath: `recordings/${userId}/raw/${mediaType}/${recordingId}.webm`,
    timestamp: record.createdAt || Date.now(),
    size: totalBytes,
    duration: 0,
    comments: [],
    regions: [],
    markers: [],
    createdAt: new Date(record.createdAt || Date.now()),
    isLocal: false,
    isStreaming: true,
    resumed: true
  };
}

export async function discardStreamingUpload(recordingId) {
  await deleteUpload(recordingId);
}
